import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ExecutionTraceState, ExecutionSynthesisChunkData } from '@shared/orchestration';
import { ExecutionTraceStore } from '@/lib/execution-trace-store';
import {
  ExecutionTraceStreamClient,
  TraceStreamError,
  TraceStreamStatus,
} from '@/lib/execution-trace-stream-client';

/**
 * Streaming phase for UI display
 */
export type StreamingPhase = 'idle' | 'planning' | 'tools' | 'synthesizing' | 'complete' | 'error';

/**
 * Tool execution summary for display
 */
export interface StreamingToolSummary {
  toolName: string;
  status: 'pending' | 'running' | 'success' | 'partial' | 'failed';
  stepId?: string;
}

/**
 * The state exposed by the streaming hook
 */
export interface ThreadsStreamingState {
  /** Current phase of the AI response */
  phase: StreamingPhase;
  /** Synthesized content accumulated so far */
  content: string;
  /** Whether streaming is currently active */
  isStreaming: boolean;
  /** Whether streaming completed successfully */
  isComplete: boolean;
  /** Error if streaming failed */
  error?: TraceStreamError;
  /** Tools that have been or are being executed */
  tools: StreamingToolSummary[];
  /** Connection status */
  connectionStatus: TraceStreamStatus;
  /** Raw trace state for advanced use cases */
  traceState: ExecutionTraceState;
}

export interface UseThreadsStreamingOptions {
  /** Session ID for the chat */
  sessionId: number;
  /** Whether streaming should be active */
  enabled?: boolean;
  /** User message to send (triggers new stream when changed) */
  message?: string;
  /** Optional project ID for context */
  projectId?: number | string;
  /** Throttle UI updates (ms) */
  throttleMs?: number;
  /** Called when streaming completes successfully */
  onComplete?: (content: string, traceState: ExecutionTraceState) => void;
  /** Called when streaming encounters a fatal error */
  onError?: (error: TraceStreamError) => void;
  /** Called when the phase changes */
  onPhaseChange?: (phase: StreamingPhase) => void;
}

export interface UseThreadsStreamingResult extends ThreadsStreamingState {
  /** Start or restart streaming */
  start: (message: string) => void;
  /** Stop streaming and clean up */
  stop: () => void;
  /** Reconnect after an error */
  reconnect: (reset?: boolean) => void;
}

const DEFAULT_THROTTLE_MS = 100;

/**
 * Determines the current streaming phase based on trace state
 */
function determinePhase(trace: ExecutionTraceState): StreamingPhase {
  if (trace.completed) {
    return trace.errors.some((e) => !e.recoverable) ? 'error' : 'complete';
  }

  if (!trace.isStreaming && !trace.plan) {
    return 'idle';
  }

  // If we have synthesis chunks, we're synthesizing
  if (trace.synthesisChunks.length > 0) {
    return 'synthesizing';
  }

  // If we have tool results or steps running, we're in tool phase
  const hasActiveTools = trace.toolResults.some(
    (r) => r.status === 'partial' || !r.isFinal
  );
  const hasStepsRunning = Object.values(trace.steps).some(
    (s) => s.status === 'running' || s.status === 'pending'
  );

  if (hasActiveTools || hasStepsRunning || trace.toolResults.length > 0) {
    return 'tools';
  }

  // If we have a plan but no tools yet, we're planning
  if (trace.plan) {
    return 'planning';
  }

  return 'planning';
}

/**
 * Extracts tool summaries from trace state
 */
function extractToolSummaries(trace: ExecutionTraceState): StreamingToolSummary[] {
  const toolMap = new Map<string, StreamingToolSummary>();

  // Process tool results
  for (const result of trace.toolResults) {
    const existing = toolMap.get(result.toolName);

    // Map result status to streaming status
    let status: StreamingToolSummary['status'] = 'pending';
    if (result.isFinal) {
      status = result.status === 'success' ? 'success' : 'failed';
    } else if (result.isPartial) {
      status = 'partial';
    } else {
      status = 'running';
    }

    // Only update if this is newer or more final
    if (!existing || result.isFinal || (!existing.status.includes('success') && !existing.status.includes('failed'))) {
      toolMap.set(result.toolName, {
        toolName: result.toolName,
        status,
        stepId: result.metadata?.stepId,
      });
    }
  }

  return Array.from(toolMap.values());
}

/**
 * Concatenates synthesis chunks into content string
 */
function assembleSynthesisContent(chunks: ExecutionSynthesisChunkData[]): string {
  return chunks.map((c) => c.content).join('');
}

/**
 * Hook for streaming AI responses in Threads
 *
 * This hook manages the SSE connection to the streaming orchestrator endpoint,
 * processes events, and exposes a simplified state for UI rendering.
 */
export const useThreadsStreaming = (
  options: UseThreadsStreamingOptions
): UseThreadsStreamingResult => {
  const {
    sessionId,
    enabled = false,
    message,
    projectId,
    throttleMs = DEFAULT_THROTTLE_MS,
    onComplete,
    onError,
    onPhaseChange,
  } = options;

  // Track the current message to detect changes
  const currentMessageRef = useRef<string | undefined>(message);
  const [streamingMessage, setStreamingMessage] = useState<string | undefined>();

  // Store for trace state
  const store = useMemo(() => new ExecutionTraceStore(), [sessionId]);

  // State
  const [status, setStatus] = useState<TraceStreamStatus>('idle');
  const [error, setError] = useState<TraceStreamError | undefined>();
  const [trace, setTrace] = useState<ExecutionTraceState>(store.getState());

  // Throttling
  const latestStateRef = useRef<ExecutionTraceState>(store.getState());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFlushRef = useRef(false);
  const previousPhaseRef = useRef<StreamingPhase>('idle');

  // [FIX] Store callbacks in refs to prevent client recreation on every render
  // This fixes the bug where 30+ SSE connections were created for a single message
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const onPhaseChangeRef = useRef(onPhaseChange);

  // Keep refs updated with latest callbacks
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
    onPhaseChangeRef.current = onPhaseChange;
  }, [onComplete, onError, onPhaseChange]);

  const clearFlushTimer = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, []);

  // [FIX] flush no longer depends on onComplete/onPhaseChange - uses refs instead
  const flush = useCallback(() => {
    pendingFlushRef.current = false;
    const currentState = latestStateRef.current;
    const newPhase = determinePhase(currentState);
    
    setTrace(currentState);

    // Check for phase changes
    if (newPhase !== previousPhaseRef.current) {
      previousPhaseRef.current = newPhase;
      onPhaseChangeRef.current?.(newPhase);
    }

    // Check for completion
    if (currentState.completed && !currentState.errors.some((e) => !e.recoverable)) {
      const content = assembleSynthesisContent(currentState.synthesisChunks);
      onCompleteRef.current?.(content, currentState);
    }
  }, []); // No dependencies - uses refs

  // [FIX] scheduleFlush no longer changes when callbacks change
  const scheduleFlush = useCallback(() => {
    if (pendingFlushRef.current) return;
    pendingFlushRef.current = true;
    clearFlushTimer();
    flushTimerRef.current = setTimeout(flush, throttleMs);
  }, [flush, throttleMs, clearFlushTimer]);

  // [FIX] Store scheduleFlush in a ref so client can always access the latest version
  // without needing to recreate the client when throttleMs changes
  const scheduleFlushRef = useRef(scheduleFlush);
  useEffect(() => {
    scheduleFlushRef.current = scheduleFlush;
  }, [scheduleFlush]);

  // Cleanup flush timer on unmount
  useEffect(() => () => clearFlushTimer(), [clearFlushTimer]);

  // Build query params for the stream
  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (streamingMessage) {
      p.message = streamingMessage;
    }
    if (projectId !== undefined && projectId !== null) {
      p.projectId = String(projectId);
    }
    return p;
  }, [streamingMessage, projectId]);

  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  // [FIX] Create the streaming client - removed unstable callback dependencies
  // Only recreate when sessionId, streamingMessage, or projectId changes
  const client = useMemo(() => {
    if (!streamingMessage) return null;

    return new ExecutionTraceStreamClient({
      store,
      options: {
        sessionId,
        messageId: 0, // Not used for new messages
        basePath: '/api/smart-chat/v2/orchestrate/stream',
        params,
        withCredentials: true,
        retry: {
          initial: 2000,
          multiplier: 1.8,
          max: 15000,
        },
        // Don't add lastEventId to query params - the v2 endpoint expects it as
        // an HTTP header (Last-Event-ID) which EventSource handles automatically
        useLastEventId: false,
      },
      onStateChange: (state, nextStatus) => {
        latestStateRef.current = state;
        setStatus(nextStatus);
        // [FIX] Use ref to always get latest scheduleFlush (handles throttleMs changes)
        scheduleFlushRef.current();
      },
      // [FIX] Use ref for onError to prevent client recreation when callback changes
      onError: (err) => {
        setError(err);
        onErrorRef.current?.(err);
      },
    });
    // [FIX] Removed scheduleFlush and onError from dependencies - they caused 30+ reconnections
    // scheduleFlush is stable now (no callback deps), and onError uses ref
  }, [store, sessionId, paramsKey]);

  // Connect/disconnect based on enabled state and message
  useEffect(() => {
    // If conditions not met, don't connect but still return cleanup
    // This ensures symmetric cleanup - if client exists, it will be disposed
    // when deps change, preventing resource leaks when enabled toggles
    if (!enabled || !client || !streamingMessage) {
      setStatus('closed');
      // Always return cleanup to handle the case where enabled toggles back
      // Without this, a disposed client could be reused on re-enable
      return () => {
        // Dispose client if it exists - safe to call even if not connected
        client?.dispose();
      };
    }

    client.connect();
    // Dispose in cleanup - this runs when deps change or component unmounts
    return () => client.dispose();
  }, [client, enabled, streamingMessage]);

  // Handle message changes
  useEffect(() => {
    if (message && message !== currentMessageRef.current) {
      currentMessageRef.current = message;
      // Reset state for new message
      store.reset();
      setTrace(store.getState());
      setError(undefined);
      previousPhaseRef.current = 'idle';
      setStreamingMessage(message);
    }
  }, [message, store]);

  // Start streaming with a new message
  const start = useCallback((newMessage: string) => {
    store.reset();
    setTrace(store.getState());
    setError(undefined);
    previousPhaseRef.current = 'idle';
    currentMessageRef.current = newMessage;
    setStreamingMessage(newMessage);
  }, [store]);

  // Stop streaming
  const stop = useCallback(() => {
    client?.dispose();
    setStatus('closed');
    setStreamingMessage(undefined);
  }, [client]);

  // Reconnect after error
  const reconnect = useCallback((reset?: boolean) => {
    if (!client) return;
    setError(undefined);
    if (reset) {
      store.reset();
      setTrace(store.getState());
      previousPhaseRef.current = 'idle';
    }
    client.reconnect(reset);
  }, [client, store]);

  // Derived state
  const phase = determinePhase(trace);
  const content = assembleSynthesisContent(trace.synthesisChunks);
  const tools = extractToolSummaries(trace);
  const isStreaming = trace.isStreaming;
  const isComplete = trace.completed && !trace.errors.some((e) => !e.recoverable);

  return {
    phase,
    content,
    isStreaming,
    isComplete,
    error,
    tools,
    connectionStatus: status,
    traceState: trace,
    start,
    stop,
    reconnect,
  };
};
